/**
 * Famous People Service
 * Uses Wikidata API for structured data (DOB/DOD) and Wikipedia API for biography text.
 * Both APIs are free, require no API key.
 */

const WIKIDATA_API = 'https://www.wikidata.org/w/api.php';

// Curated Wikipedia editions shown only when Wikidata confirms that the person
// has an article there. No translation service or added dependency is needed.
const WIKIPEDIA_LANGUAGES = [
  { code: 'en', site: 'enwiki', label: 'English' },
  { code: 'es', site: 'eswiki', label: 'Español' },
  { code: 'fr', site: 'frwiki', label: 'Français' },
  { code: 'de', site: 'dewiki', label: 'Deutsch' },
  { code: 'pt', site: 'ptwiki', label: 'Português' },
  { code: 'vi', site: 'viwiki', label: 'Tiếng Việt' },
  { code: 'it', site: 'itwiki', label: 'Italiano' },
  { code: 'he', site: 'hewiki', label: 'עברית' },
  { code: 'ar', site: 'arwiki', label: 'العربية' },
  { code: 'ru', site: 'ruwiki', label: 'Русский' },
  { code: 'zh', site: 'zhwiki', label: '中文' },
  { code: 'ja', site: 'jawiki', label: '日本語' },
  { code: 'ko', site: 'kowiki', label: '한국어' },
  { code: 'hi', site: 'hiwiki', label: 'हिन्दी' },
];

const getWikipediaLanguage = (code) => (
  WIKIPEDIA_LANGUAGES.find(language => language.code === code) || null
);

const buildWikipediaUrl = (language, title) => (
  `https://${language}.wikipedia.org/wiki/${encodeURIComponent(title.replace(/ /g, '_'))}`
);

/**
 * Search for people by name using Wikidata's entity search.
 * Returns up to `limit` results with id, label, and description.
 * Filters to only return real humans (instance of Q5).
 */
export async function searchPeople(query, limit = 10) {
  if (!query || query.trim().length < 2) return [];

  const params = new URLSearchParams({
    action: 'wbsearchentities',
    search: query.trim(),
    language: 'en',
    format: 'json',
    type: 'item',
    limit: '20', // Fetch more to account for filtering
    origin: '*',
  });

  try {
    const response = await fetch(`${WIKIDATA_API}?${params}`);
    const data = await response.json();

    if (!data.search || data.search.length === 0) return [];

    const candidates = data.search.map((item) => ({
      id: item.id,
      name: item.label || item.display?.label?.value || '',
      description: item.description || item.display?.description?.value || '',
    }));

    // Filter to only humans by checking P31 (instance of) = Q5 (human)
    const humanIds = await filterHumans(candidates.map((c) => c.id));
    
    return candidates
      .filter((c) => humanIds.has(c.id))
      .slice(0, limit);
  } catch (error) {
    console.error('Wikidata search error:', error);
    return [];
  }
}

/**
 * Check which entity IDs are humans (P31 = Q5) via a batch request.
 * Returns a Set of IDs that are human.
 */
async function filterHumans(ids) {
  if (!ids || ids.length === 0) return new Set();

  const params = new URLSearchParams({
    action: 'wbgetentities',
    ids: ids.join('|'),
    props: 'claims',
    format: 'json',
    origin: '*',
  });

  try {
    const response = await fetch(`${WIKIDATA_API}?${params}`);
    const data = await response.json();

    const humanSet = new Set();
    if (!data.entities) return humanSet;

    for (const [id, entity] of Object.entries(data.entities)) {
      const p31 = entity.claims?.P31;
      if (!p31) continue;
      // Check if any P31 value is Q5 (human)
      const isHuman = p31.some(
        (claim) => claim.mainsnak?.datavalue?.value?.id === 'Q5'
      );
      if (isHuman) humanSet.add(id);
    }

    return humanSet;
  } catch (error) {
    console.error('Error filtering humans:', error);
    // On error, return all IDs (don't filter)
    return new Set(ids);
  }
}

/**
 * Get detailed person info: DOB, DOD, occupation, and Wikipedia biography.
 * Uses Wikidata for structured dates and Wikipedia for the intro text.
 */
export async function getPersonDetails(wikidataId) {
  try {
    // Fetch claims from Wikidata (DOB, DOD, occupation, Wikipedia link)
    const claimsParams = new URLSearchParams({
      action: 'wbgetentities',
      ids: wikidataId,
      props: 'claims|sitelinks|labels|descriptions',
      languages: 'en',
      sitefilter: WIKIPEDIA_LANGUAGES.map(language => language.site).join('|'),
      format: 'json',
      origin: '*',
    });

    const claimsResponse = await fetch(`${WIKIDATA_API}?${claimsParams}`);
    const claimsData = await claimsResponse.json();

    const entity = claimsData.entities?.[wikidataId];
    if (!entity) return null;

    const claims = entity.claims || {};
    const name = entity.labels?.en?.value || '';
    const description = entity.descriptions?.en?.value || '';

    // Extract DOB (P569)
    const dob = extractDate(claims.P569);

    // Extract DOD (P570)
    const dod = extractDate(claims.P570);

    // Extract occupation (P106)
    const occupationIds = extractEntityIds(claims.P106, 3);

    // Only expose language editions that actually have a person article.
    const wikipediaLanguages = WIKIPEDIA_LANGUAGES.flatMap((language) => {
      const title = entity.sitelinks?.[language.site]?.title;
      return title ? [{ code: language.code, label: language.label, title }] : [];
    });
    const initialWikipedia = wikipediaLanguages.find(language => language.code === 'en')
      || wikipediaLanguages[0]
      || null;
    const englishWikipedia = wikipediaLanguages.find(language => language.code === 'en') || null;

    // Load the default biography from a real Wikipedia language edition.
    let biographyHtml = '';
    if (initialWikipedia) {
      biographyHtml = await getWikipediaBio(initialWikipedia.title, initialWikipedia.code);
    }

    const biographyBlocks = parseBiographyHtml(biographyHtml);

    // Keep timeline extraction tied to English source text. Switching biography
    // languages later never mutates the established timeline.
    const timelineHtml = englishWikipedia && initialWikipedia?.code !== 'en'
      ? await getWikipediaBio(englishWikipedia.title, 'en')
      : biographyHtml;
    const timeline = buildTimeline(stripHtml(timelineHtml), dob, dod);

    // Resolve occupation labels
    let occupations = [];
    if (occupationIds.length > 0) {
      occupations = await resolveEntityLabels(occupationIds);
    }

    return {
      id: wikidataId,
      name,
      description,
      dob,
      dod,
      occupations,
      biographyBlocks,
      biographyLanguage: initialWikipedia?.code || null,
      wikipediaLanguages,
      timeline,
      wikiTitle: initialWikipedia?.title || null,
      wikiUrl: initialWikipedia
        ? buildWikipediaUrl(initialWikipedia.code, initialWikipedia.title)
        : null,
    };
  } catch (error) {
    console.error('Error fetching person details:', error);
    return null;
  }
}

/**
 * Extract a date from a Wikidata time claim (P569 or P570).
 * Returns { year, month, day, iso } or null.
 */
function extractDate(claim) {
  if (!claim || !claim[0]) return null;

  const mainsnak = claim[0].mainsnak;
  if (!mainsnak || mainsnak.snaktype !== 'value') return null;

  const timeValue = mainsnak.datavalue?.value;
  if (!timeValue || !timeValue.time) return null;

  // Wikidata time format: "+1879-03-14T00:00:00Z"
  const timeStr = timeValue.time;
  const precision = timeValue.precision; // 11=day, 10=month, 9=year

  // Parse the date string
  const match = timeStr.match(/^[+-]?(\d+)-(\d{2})-(\d{2})/);
  if (!match) return null;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);

  // Handle BC dates (negative sign)
  const isBC = timeStr.startsWith('-');
  const displayYear = isBC ? -year : year;

  return {
    year: displayYear,
    month: precision >= 10 ? month : null,
    day: precision >= 11 ? day : null,
    precision,
    iso: precision >= 11
      ? `${displayYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T12:00:00.000Z`
      : null,
    formatted: formatDate(displayYear, precision >= 10 ? month : null, precision >= 11 ? day : null),
  };
}

/**
 * Format a date for display.
 */
function formatDate(year, month, day) {
  const months = [
    '', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const yearStr = year < 0 ? `${Math.abs(year)} BC` : `${year}`;

  if (!month) return yearStr;
  if (!day) return `${months[month]} ${yearStr}`;
  return `${months[month]} ${day}, ${yearStr}`;
}

/**
 * Extract entity IDs from a claim array (for things like occupation).
 */
function extractEntityIds(claim, limit = 3) {
  if (!claim) return [];
  return claim
    .slice(0, limit)
    .map((c) => c.mainsnak?.datavalue?.value?.id)
    .filter(Boolean);
}

/**
 * Resolve Wikidata entity IDs to their English labels.
 */
async function resolveEntityLabels(ids) {
  if (!ids || ids.length === 0) return [];

  const params = new URLSearchParams({
    action: 'wbgetentities',
    ids: ids.join('|'),
    props: 'labels',
    languages: 'en',
    format: 'json',
    origin: '*',
  });

  try {
    const response = await fetch(`${WIKIDATA_API}?${params}`);
    const data = await response.json();

    return ids
      .map((id) => data.entities?.[id]?.labels?.en?.value)
      .filter(Boolean);
  } catch (error) {
    console.error('Error resolving entity labels:', error);
    return [];
  }
}

/**
 * Get the intro biography HTML from Wikipedia (includes <b>, <i> tags).
 */
async function getWikipediaBio(title, language = 'en') {
  const supportedLanguage = getWikipediaLanguage(language);
  if (!supportedLanguage || !title) return '';

  const params = new URLSearchParams({
    action: 'query',
    titles: title,
    prop: 'extracts',
    exintro: 'true',
    format: 'json',
    origin: '*',
  });

  try {
    const apiUrl = `https://${supportedLanguage.code}.wikipedia.org/w/api.php`;
    const response = await fetch(`${apiUrl}?${params}`);
    if (!response.ok) return '';

    const data = await response.json();
    const pages = data.query?.pages;
    if (!pages) return '';

    const pageId = Object.keys(pages)[0];
    if (!pageId || pageId === '-1') return '';

    return pages[pageId].extract || '';
  } catch (error) {
    console.error('Wikipedia bio fetch error:', error);
    return '';
  }
}

/**
 * Fetch one real Wikipedia language edition for an existing person article.
 * The caller supplies the exact localized title returned by Wikidata.
 */
export async function getLocalizedBiography(language, title) {
  const supportedLanguage = getWikipediaLanguage(language);
  if (!supportedLanguage || !title) return null;

  const biographyHtml = await getWikipediaBio(title, supportedLanguage.code);
  const biographyBlocks = parseBiographyHtml(biographyHtml);
  if (biographyBlocks.length === 0) return null;

  return {
    biographyBlocks,
    biographyLanguage: supportedLanguage.code,
    wikiTitle: title,
    wikiUrl: buildWikipediaUrl(supportedLanguage.code, title),
  };
}

/**
 * Strip HTML tags to get plain text.
 */
function stripHtml(html) {
  return html
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Parse Wikipedia HTML into an array of paragraphs.
 * Each paragraph is an array of styled segments: { text, bold, italic }.
 * This gives us rich formatting in React Native without a heavy HTML renderer.
 */
function parseBiographyHtml(html) {
  if (!html) return [];

  // Extract content between <p> tags (handles class attributes)
  const paragraphs = [];
  const pTagRegex = /<p[^>]*>([\s\S]*?)<\/p>/g;
  let pMatch;
  while ((pMatch = pTagRegex.exec(html)) !== null) {
    const content = pMatch[1].trim();
    if (!content || content === '\n' || content === '\n\n\n') continue;
    
    const segments = parseInlineHtml(content);
    // Filter out paragraphs with no visible text
    const hasVisibleText = segments.some((s) => s.text.trim().length > 0);
    if (hasVisibleText) {
      paragraphs.push(segments);
    }
  }

  return paragraphs;
}

/**
 * Parse inline HTML (within a paragraph) into styled segments.
 * Handles <b>, <i>, nested <b><i>, and strips other tags.
 */
function parseInlineHtml(html) {
  const segments = [];
  let current = '';
  let bold = false;
  let italic = false;
  let i = 0;

  while (i < html.length) {
    // Handle newlines as spaces
    if (html[i] === '\n') {
      current += ' ';
      i++;
      continue;
    }

    if (html[i] === '<') {
      // Find end of tag
      const closeIdx = html.indexOf('>', i);
      if (closeIdx === -1) {
        current += html[i];
        i++;
        continue;
      }

      const tagRaw = html.substring(i, closeIdx + 1);
      const tag = tagRaw.toLowerCase();

      // Check if this is a bold or italic tag before flushing
      const isBoldOpen = tag === '<b>' || tag.match(/^<b\s/);
      const isBoldClose = tag === '</b>';
      const isItalicOpen = tag === '<i>' || tag.match(/^<i\s/);
      const isItalicClose = tag === '</i>';

      // Flush current text before changing bold/italic state
      if (isBoldOpen || isBoldClose || isItalicOpen || isItalicClose) {
        if (current) {
          segments.push({ text: decodeHtmlEntities(current), bold, italic });
          current = '';
        }
        if (isBoldOpen) bold = true;
        if (isBoldClose) bold = false;
        if (isItalicOpen) italic = true;
        if (isItalicClose) italic = false;
      }
      // All other tags are stripped silently (spans, sup, sub, etc.)

      i = closeIdx + 1;
    } else {
      current += html[i];
      i++;
    }
  }

  // Flush remaining text
  if (current) {
    segments.push({ text: decodeHtmlEntities(current), bold, italic });
  }

  // Clean up: merge adjacent segments with same style, remove empty ones
  const cleaned = [];
  for (const seg of segments) {
    const text = seg.text.replace(/\s+/g, ' ');
    if (!text || text === ' ' && cleaned.length === 0) continue;
    
    const prev = cleaned[cleaned.length - 1];
    if (prev && prev.bold === seg.bold && prev.italic === seg.italic) {
      prev.text += text;
    } else {
      cleaned.push({ text, bold: seg.bold, italic: seg.italic });
    }
  }

  // Trim trailing space from last segment
  if (cleaned.length > 0) {
    cleaned[cleaned.length - 1].text = cleaned[cleaned.length - 1].text.trimEnd();
  }

  return cleaned;
}

/**
 * Decode common HTML entities.
 */
function decodeHtmlEntities(str) {
  return str
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&#(\d+);/g, (_, num) => String.fromCharCode(parseInt(num, 10)));
}

/**
 * Build a timeline of key dated events from biography text.
 * Extracts patterns like "In 1995,", "on June 28, 1971", "in March 2000", etc.
 * Returns array of { year, month, day, displayDate, event, iso } sorted chronologically.
 */
function buildTimeline(plainText, dob, dod) {
  const events = [];
  const seenKeys = new Set();
  const monthPattern = 'January|February|March|April|May|June|July|August|September|October|November|December';

  // Work sentence-by-sentence so an event always retains its complete Wikipedia
  // sentence instead of becoming the words that happened to follow a date.
  const sentences = plainText
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/([.!?])\s+(?=["“'‘(]*[A-Z])/g, '$1\n')
    .split('\n')
    .map(sentence => sentence.trim())
    .filter(Boolean);

  const isSameStructuredDate = (date, year, month, day) => Boolean(
    date
    && date.year === year
    && (date.month ?? null) === (month ?? null)
    && (date.day ?? null) === (day ?? null)
  );

  const addEvent = (year, month, day, displayDate, sentence) => {
    if (year < 500 || year > 2100) return;
    if (isSameStructuredDate(dob, year, month, day)) return;
    if (isSameStructuredDate(dod, year, month, day)) return;

    const eventText = truncateEvent(sentence);
    const key = `${year}-${month ?? 0}-${day ?? 0}-${eventText}`;
    if (seenKeys.has(key)) return;
    seenKeys.add(key);

    events.push({
      year,
      month,
      day,
      displayDate,
      event: eventText,
      iso: `${year}-${String(month || 1).padStart(2, '0')}-${String(day || 1).padStart(2, '0')}T12:00:00.000Z`,
    });
  };

  for (const sentence of sentences) {
    const matches = [];
    const occupiedRanges = [];
    let match;

    // Most precise first: "August 14, 2026".
    const fullDatePattern = new RegExp(
      `\\b(${monthPattern})\\s+(\\d{1,2}),?\\s+(\\d{4})\\b`,
      'gi'
    );
    while ((match = fullDatePattern.exec(sentence)) !== null) {
      const month = getMonthNumber(match[1]);
      const day = parseInt(match[2], 10);
      const year = parseInt(match[3], 10);
      if (day < 1 || day > 31) continue;

      occupiedRanges.push([match.index, fullDatePattern.lastIndex]);
      matches.push({
        index: match.index,
        year,
        month,
        day,
        displayDate: formatDate(year, month, day),
      });
    }

    // Month precision: "March 2000".
    const monthYearPattern = new RegExp(`\\b(${monthPattern})\\s+(\\d{4})\\b`, 'gi');
    while ((match = monthYearPattern.exec(sentence)) !== null) {
      const overlapsMorePreciseDate = occupiedRanges.some(
        ([start, end]) => match.index < end && monthYearPattern.lastIndex > start
      );
      if (overlapsMorePreciseDate) continue;

      const month = getMonthNumber(match[1]);
      const year = parseInt(match[2], 10);
      occupiedRanges.push([match.index, monthYearPattern.lastIndex]);
      matches.push({
        index: match.index,
        year,
        month,
        day: null,
        displayDate: formatDate(year, month, null),
      });
    }

    // Year precision: "in 2016". Ignore years already included in a fuller date.
    const yearPattern = /\b(\d{4})\b/g;
    while ((match = yearPattern.exec(sentence)) !== null) {
      const overlapsMorePreciseDate = occupiedRanges.some(
        ([start, end]) => match.index < end && yearPattern.lastIndex > start
      );
      if (overlapsMorePreciseDate) continue;

      const year = parseInt(match[1], 10);
      matches.push({
        index: match.index,
        year,
        month: null,
        day: null,
        displayDate: formatDate(year, null, null),
      });
    }

    matches
      .sort((a, b) => a.index - b.index)
      .forEach(({ year, month, day, displayDate }) => {
        addEvent(year, month, day, displayDate, sentence);
      });
  }

  if (dob) {
    events.push({
      year: dob.year,
      month: dob.month,
      day: dob.day,
      displayDate: dob.formatted,
      event: 'Born',
      iso: dob.iso,
      isDob: true,
    });
  }

  if (dod) {
    events.push({
      year: dod.year,
      month: dod.month,
      day: dod.day,
      displayDate: dod.formatted,
      event: 'Died',
      iso: dod.iso,
      isDod: true,
    });
  }

  events.sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    if ((a.month || 0) !== (b.month || 0)) return (a.month || 0) - (b.month || 0);
    return (a.day || 0) - (b.day || 0);
  });

  return deduplicateTimeline(events).slice(0, 15);
}

/**
 * Truncate event text to reasonable length.
 */
function truncateEvent(text) {
  return text.trim();
}

/**
 * Remove near-duplicate timeline entries (same year + similar text).
 */
function deduplicateTimeline(events) {
  const result = [];
  for (const event of events) {
    const isDuplicate = result.some(
      (existing) => (
        existing.year === event.year
        && (existing.month ?? null) === (event.month ?? null)
        && (existing.day ?? null) === (event.day ?? null)
        && existing.event === event.event
      )
    );
    if (!isDuplicate) {
      result.push(event);
    }
  }
  return result;
}

/**
 * Get month number from name.
 */
function getMonthNumber(name) {
  const months = {
    january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
    july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
  };
  return months[name.toLowerCase()] || 1;
}
